<template>
  <div>
    <div class="drafts-summary">
      <SyncDashboardCard
        class="drafts-card"
        :sync-uuid="syncUuid"
        :fetching="synchronizationsDraftsMetaFetching"
        :offersAllCount="synchronizationsDraftsMeta.offersAllCount"
        :offersNotSellingCount="synchronizationsDraftsMeta.offersNotSellingCount"
        :offersWithErrorsCount="synchronizationsDraftsMeta.offersWithErrorsCount"
        :offersIsNeedActionCount="synchronizationsDraftsMeta.offersIsNeedActionCount"
        :offersIsSellingCount="synchronizationsDraftsMeta.offersIsSellingCount"
      />
      <a-button
        v-if="parseErrors.length"
        class="parse-errors-button"
        type="link"
        size="large"
        @click="parseErrorsOpened = true"
      >
        {{ $t("synchronization.parse_errors") }} ({{ parseErrors.length }})
      </a-button>
      <SyncParseErrorsModal
        v-if="parseErrorsOpened"
        :errors="parseErrors"
        @close="parseErrorsOpened = false"
      />
    </div>

    <div class="drafts">
      <div class="drafts__nav">
        <div class="drafts__left">
          <!-- RELOAD -->
          <a-button @click="refreshTable">
            <a-icon type="reload" />
            {{ $t("orders.reload") }}
          </a-button>

          <a-divider type="vertical" />

          <!-- SEARCH -->
          <a-input-search
            v-model="searchDraftValue"
            style="width: 400px"
            :placeholder="$t('synchronization.search_by_drafts')"
            @search="handleSearchDrafts"
          />
        </div>

        <div class="drafts__right">
          <!-- APPROVE SELECTED -->
          <a-button
            type="primary"
            :style="{ marginRight: '12px' }"
            :disabled="selectedKeysNeedApprove.length === 0"
            :loading="bulkUpdateLoading"
            @click="handleBulkUpdate"
          >
            {{ `${$t("synchronization.approve_selected")} (${selectedKeysNeedApprove.length})` }}
          </a-button>

          <!-- RESET FILTERS -->
          <a-button
            v-if="hasFilters"
            :style="{ marginRight: '12px' }"
            @click="handleResetAllFilters"
          >
            <a-icon type="undo" />
            {{ $t("common.reset_filters") }}
          </a-button>

          <a-dropdown :trigger="['click']">
            <a-button type="primary">
              <a-icon type="setting" />
              {{ $t("synchronization.synchronization_settings") }}
            </a-button>

            <a-menu slot="overlay">
              <a-menu-item key="1">
                <router-link :to="{ name: 'sync-settings', params: { uuid: syncUuid } }">
                  {{ $t("synchronization.edit_synchronization") }}
                </router-link>
              </a-menu-item>
              <a-menu-item
                v-if="synchronization && synchronization.is_active"
                key="stop_sync"
                @click="handleOpenStopModal(syncUuid)"
              >
                {{ $t("synchronization.stop") }}
              </a-menu-item>
              <a-menu-item
                v-else
                key="start_sync"
                @click="handleStartSync(syncUuid, updateSyncCallback)"
              >
                {{ $t("synchronization.renew") }}
              </a-menu-item>
              <a-menu-item
                key="3"
                @click="handleOpenDeleteModal(syncUuid)"
              >
                {{ $t("synchronization.delete") }}
              </a-menu-item>
            </a-menu>
          </a-dropdown>
        </div>
      </div>

      <div class="drafts__tabs">
        <a-tabs
          class="tabs"
          :class="{ 'tabs--no-margin': drafts.length === 0 }"
          :active-key="selectedTab"
          @change="handleChangeTab"
        >
          <a-tab-pane
            v-for="tab in tabs"
            :key="tab.key"
          >
            <template slot="tab">
              {{ tab.label }}
              <a-badge
                v-if="tab.key !== 'all'"
                :count="tab.count"
                :number-style="{ backgroundColor: '#389e0d' }"
                :overflowCount="999"
                class="tabs__tab-counter"
              />
            </template>
          </a-tab-pane>
        </a-tabs>
      </div>

      <a-alert
        v-if="statusFailure"
        type="error"
        banner
        style="margin-bottom: 24px"
      >
        <template slot="message">
          <i18n
            :tag="false"
            path="synchronization.price_is_not_actual"
          >
            <a
              @click.stop.prevent="chatWithModerator"
              target="_blank"
              slot="messenger_url"
            >
              {{ $t("synchronization.messenger") }}
            </a>
            <a
              @click.stop.prevent="ticketWithSupport"
              target="_blank"
              slot="support_url"
            >
              {{ $t("synchronization.support") }}
            </a>
          </i18n>
        </template>
      </a-alert>

      <div class="drafts__content">
        <a-table
          ref="table"
          size="middle"
          rowKey="uuid"
          :pagination="tablePagination"
          @change="onTableChange"
          :columns="columns"
          :dataSource="drafts"
          :scroll="{ x: true }"
          :loading="fetching || bulkUpdateLoading"
          defaultExpandAllRows
          :expandedRowKeys="drafts.map((draft) => draft.uuid)"
          :rowSelection="{
            selectedRowKeys: selectedRowKeys,
            onChange: handleSelectRows
          }"
        >
          <div
            slot="name"
            slot-scope="name, record"
          >
            <span class="drafts__xml-code">{{ $t("synchronization.xml_code") }}:</span>
            {{ record.xml_id }}
            <br />
            {{ record.name }}
          </div>

          <span slot="imagesTitle" />

          <template
            slot="images"
            slot-scope="images"
          >
            <Thumbnail
              v-if="images && images.length"
              :size="44"
              class="drafts__thumbnail"
              :images="images"
            />
            <div
              v-else
              class="drafts__thumbnail"
            >
              <a-icon type="warning" />
            </div>
          </template>

          <div
            slot="quantity"
            slot-scope="quantity"
          >
            <span
              :class="{
                'drafts__empty-quantity': quantity === 0
              }"
            >
              {{ $tc("synchronization.quantity", quantity) }}
            </span>
          </div>

          <div
            slot="price"
            slot-scope="price, record"
          >
            {{
              price === null
                ? ""
                : `${getFormattedPrice(price)} ${record.currency.name.toLowerCase()}`
            }}
          </div>

          <div
            class="drafts__status"
            id="draft__status_target"
            slot="status"
            slot-scope="status, record"
          >
            <div class="drafts__status-row">
              <span
                :class="[
                  record.offer && record.offer.is_sells
                    ? 'drafts__for-sale'
                    : 'drafts__not-for-sale'
                ]"
              >
                {{
                  record.offer && record.offer.is_sells
                    ? $t("synchronization.for_sale")
                    : $t("synchronization.not_for_sale")
                }}
              </span>

              <!-- DRAFT ERRORS -->
              <span
                v-if="hasProperties(record.errors)"
                class="drafts__status-error"
              >
                <a-popover placement="topRight">
                  <span
                    slot="content"
                    style="max-width: 300px"
                  >
                    <DescriptionItem
                      v-for="key in Object.keys(record.errors)"
                      :key="key"
                      :title="key"
                      :content="record.errors[key]"
                      style="max-width: 300px"
                    />
                  </span>

                  <template slot="title">
                    <span>
                      {{ $t("synchronization.draft_error") }}
                    </span>
                  </template>

                  <a-tag color="red">
                    {{ $t("synchronization.xml_error") }}
                  </a-tag>
                </a-popover>
              </span>
              <span
                v-else-if="hasProperties(record.warnings)"
                class="drafts__status-error"
              >
                <a-popover placement="topRight">
                  <span
                    slot="content"
                    style="max-width: 300px"
                  >
                    <DescriptionItem
                      v-for="key in Object.keys(record.warnings)"
                      :key="key"
                      :title="key"
                      :content="record.warnings[key]"
                      style="max-width: 300px"
                    />
                  </span>

                  <template slot="title">
                    <span>
                      {{ $t("synchronization.draft_error") }}
                    </span>
                  </template>

                  <a-tag color="orange">
                    {{ $t("synchronization.xml_warning") }}
                  </a-tag>
                </a-popover>
              </span>
            </div>

            <div class="drafts__status-subrow">
              <span
                v-if="record.offer && record.is_need_action"
                class="drafts__need-action"
              >
                {{ $t("synchronization.approve_again") }}
              </span>

              <span
                v-if="!record.product"
                class="drafts__need-action"
              >
                {{ $t("synchronization.match_and_approve") }}
              </span>

              <span
                v-if="!record.offer && record.product && record.is_need_action"
                class="drafts__need-action"
              >
                {{ $t("synchronization.approve_matched_good") }}
              </span>

              <span
                v-if="
                  !record.offer &&
                  record.product &&
                  !record.is_need_action &&
                  record.status.key === 'APPROVED' &&
                  !hasProperties(record.errors)
                "
                class="drafts__pending-offer"
              >
                <a-icon type="clock-circle" />
                {{ $t("synchronization.offer_awaiting_creation") }}
              </span>

              <!-- OFFER ERRORS -->
              <span v-if="record.offer && record.offer.not_available_reasons.length">
                {{ record.offer.not_available_reasons.join(". ") }}
              </span>
            </div>
          </div>

          <!-- EXPANDED ROW -->
          <template
            slot="expandedRowRender"
            slot-scope="draft"
          >
            <SyncDraftsExpandedRow
              :draft="draft"
              :ref="(el) => setDraftRef(el, draft.uuid)"
              :delivery="draft.delivery.uuid"
              :warranty="draft.warranty"
              :isLocked="draft.is_locked"
              :isNeedAction="draft.is_need_action"
              :synchronizationUuid="syncUuid"
              :conditionValue="getConditionValueByDraft(draft)"
              @changeProduct="handleChangeProduct"
              @showApproveWindow="handleShowApproveWindow"
              @offerDeleted="onOfferDeleted"
            />
          </template>
        </a-table>

        <!-- SEARCH PRODUCT -->

        <a-drawer
          :visible="searchProductsIsOpened"
          :title="$t('product_catalog.search_product')"
          width="60%"
          destroyOnClose
          @close="closeSearchProductsDrawer"
        >
          <SearchProducts
            @selected="handleProductSelect"
            :initialValue="currentDraftName"
          />
        </a-drawer>

        <ApproveWindow
          :syncUuid="syncUuid"
          :payload="approveWindowPayload"
          :is-opened="approveWindowisOpened"
          @close="handleCloseApproveWindow"
          @approved="handleDraftApproved"
        />

        <DeleteSyncModal
          :is-opened="isDeleteModalOpened"
          :sync-uuid="currentSyncUuid"
          @close="handleCloseDeleteModal"
          @delete="router.push({ name: 'sync-list' })"
        />

        <StopSyncModal
          :is-opened="isStopModalOpened"
          :sync-uuid="currentSyncUuid"
          @close="handleCloseStopModal"
          @stop="updateSyncCallback"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { message } from "ant-design-vue"
import { storeToRefs } from "pinia"
import { computed, ref } from "vue"
import { getCurrentInstance, onBeforeUpdate, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router/composables"

import DescriptionItem from "@/components/Common/DescriptionItem.vue"
import SearchProducts from "@/components/Common/SearchProducts"
import { SearchedProduct } from "@/components/Common/SearchProducts/SearchProducts.vue"
import Thumbnail from "@/components/Common/Thumbnail"
import { FetcherParams, useAntTable } from "@/composables/useAntTable"
import i18n from "@/i18n"
import ApproveWindow from "@/modules/sync/_components/ApproveWindow"
import DeleteSyncModal from "@/modules/sync/_components/DeleteSyncModal.vue"
import StopSyncModal from "@/modules/sync/_components/StopSyncModal.vue"
import SyncDashboardCard from "@/modules/sync/_components/SyncDashboardCard.vue"
import SyncDraftsExpandedRow from "@/modules/sync/_components/SyncDraftsExpandedRow.vue"
import SyncParseErrorsModal from "@/modules/sync/_components/SyncParseErrorsModal.vue"
import { useSyncDraftsStore } from "@/modules/sync/store/useSyncDraftsStore"
import { useSyncMetaStore } from "@/modules/sync/store/useSyncMetaStore"
import { useSyncStore } from "@/modules/sync/store/useSyncStore"
import { getSyncTaskStatus, isTerminalSyncTaskStatus } from "@/modules/sync/syncTaskStatus"
import syncService from "@/services/syncService"
import { HANDLE_SUPPORT_DRAWER } from "@/store/modules/notifications/mutationTypes"
import { SyncDraft } from "@/types/sync.type"
import getFormattedPrice from "@/utils/getFormattedPrice"
import getProductState from "@/utils/getProductState"

import { useSyncSettings } from "../useSyncSettings"

import { buildDraftsQuery } from "./syncDraftFilters"
import { useSyncDraftsColumns } from "./useSyncDraftsColumns"

// Fix: Define dummy variables to hint types for Volar in legacy slot usage
// This is required because Volar cannot infer slot-scope types in usage of AntDV 1.x with script setup
const record = {} as SyncDraft
const images = [] as string[]
const quantity = 0 as number
const price = 0 as number
const draft = {} as SyncDraft
// End Fix

// Store & Route
const syncStore = useSyncStore()
const syncDraftsStore = useSyncDraftsStore()
const syncMetaStore = useSyncMetaStore()
const route = useRoute()
const router = useRouter()

const { synchronization, synchronizationsDraftsMeta, synchronizationsDraftsMetaFetching } =
  storeToRefs(syncStore)
const { drafts, count, fetching } = storeToRefs(syncDraftsStore)
const { proxy } = getCurrentInstance()!
// End Store & Route

// State
const selectedTab = ref("all")
const searchProductsIsOpened = ref(false)
const approveWindowisOpened = ref(false)
const searchDraftValue = ref("")
const selectedRowKeys = ref([])
const currentDraftName = ref("")
const currentDraftUuid = ref(null)
const approveWindowPayload = ref<SyncDraft | null>(null)
const draftRefs = ref<Record<string, any>>({})

const syncUuid = route.params.uuid

const bulkUpdateLoading = ref(false)
const parseErrorsOpened = ref(false)

const parseErrors = computed(() => synchronization.value?.errors?.parse_errors || [])
// End State

// Composables
const {
  isDeleteModalOpened,
  isStopModalOpened,
  currentSyncUuid,
  handleOpenStopModal,
  handleOpenDeleteModal,
  handleCloseStopModal,
  handleCloseDeleteModal,
  handleStartSync
} = useSyncSettings()
const { columns } = useSyncDraftsColumns()

const fetchDrafts = (params: FetcherParams) => {
  const { page, pageSize, ordering, filters } = params

  return syncDraftsStore.getDrafts({
    page,
    pageSize,
    uuid: syncUuid,
    query: buildDraftsQuery({
      filters,
      ordering,
      selectedTab: selectedTab.value,
      searchDraftValue: searchDraftValue.value
    })
  })
}

const { hasTableFilters, resetTableFilters, tablePagination, onTableChange, refreshTable } =
  useAntTable({
    key: "drafts",
    position: "both",
    total: count,
    fetcher: fetchDrafts
  })
// End Composables

// Computed
const selectedKeysNeedApprove = computed(() => {
  const selectedDraftsNeedApprove = drafts.value
    .filter((draft) => selectedRowKeys.value.includes(draft.uuid))
    .filter((draft) => {
      const isNeedAction = draft.is_need_action
      const withProduct = draft.product !== null
      const isInvalid = draft.status.key === "INVALID"

      return isNeedAction && withProduct && !isInvalid
    })

  return selectedDraftsNeedApprove.map((draft) => draft.uuid)
})
const hasFilters = computed(() => {
  return hasTableFilters.value || searchDraftValue.value.length > 0 || selectedTab.value !== "all"
})
const statusFailure = computed(() => {
  return synchronization.value && synchronization.value.status === "FAILURE"
})
const tabs = computed(() => {
  return [
    {
      label: i18n.t("synchronization.tab_all"),
      key: "all"
    },
    {
      label: i18n.t("synchronization.need_action"),
      key: "is_need_action",
      count: synchronizationsDraftsMeta.value.offersIsNeedActionCount
    }
  ]
})
// End Computed

// Methods
const handleBulkUpdate = () => {
  const payload = selectedKeysNeedApprove.value.map((uuid) => ({
    uuid,
    _approve: true
  }))

  bulkUpdateLoading.value = true

  syncDraftsStore
    .updateDrafts({
      payload,
      synchronizationUuid: syncUuid
    })
    .then((response) => {
      pollSyncTask(response.meta.task_id)
    })
    .catch((error) => {
      message.error(i18n.t("synchronization.drafts_not_updated") as string)
      bulkUpdateLoading.value = false
    })
    .finally(() => {
      selectedRowKeys.value = []
    })
}
const pollSyncTask = async (taskId: string): Promise<string | null> => {
  if (!taskId) return null

  const poll = async (): Promise<string | null> => {
    try {
      const response = await syncService.trackTask({
        synchronizationUuid: syncUuid,
        taskUuid: taskId
      })

      if (isTerminalSyncTaskStatus(getSyncTaskStatus(response.data))) {
        refreshTable()
        syncStore.getSyncDraftsMeta(syncUuid)
        bulkUpdateLoading.value = false
      } else {
        // Чекаємо pollInterval і потім рекурсивно викликаємо poll
        await new Promise((resolve) => setTimeout(resolve, 3000))
        return poll()
      }
    } catch (error) {
      refreshTable()
      syncStore.getSyncDraftsMeta(syncUuid)
      message.error(i18n.t("synchronization.drafts_not_updated") as string)
      bulkUpdateLoading.value = false
    }
  }

  return poll()
}
const handleResetAllFilters = () => {
  searchDraftValue.value = ""
  selectedTab.value = "all"
  resetTableFilters()
}
const updateSyncCallback = () => {
  syncStore.getSynchronization(syncUuid)
}
const handleChangeTab = (key: string) => {
  selectedTab.value = key
  refreshTable()
}
const chatWithModerator = () => {
  router.push({ name: "messenger", query: { openSupport: "true" } })
}
const ticketWithSupport = () => {
  proxy.$store.commit(`notifications/${HANDLE_SUPPORT_DRAWER}`, {
    open: true
  })
}
const setDraftRef = (el: any, uuid: string) => {
  if (el) {
    draftRefs.value[`draft_${uuid}`] = el
  }
}
const hasProperties = (object: Record<string, unknown>) => Object.keys(object).length > 0
const getConditionValueByDraft = (draft: SyncDraft): any => {
  const { condition_key: backKey, condition_value: backValue } = draft
  const state = getProductState(backKey, backValue)

  return `${state.backKey}_${state.value}`
}
const handleChangeProduct = ({ draftUuid, draft }: { draftUuid: string; draft: SyncDraft }) => {
  currentDraftUuid.value = draftUuid
  currentDraftName.value = draft.name
  searchProductsIsOpened.value = true
}
const handleShowApproveWindow = ({ draft }: { draft: SyncDraft }) => {
  approveWindowPayload.value = draft
  approveWindowisOpened.value = true
}
const handleSearchDrafts = () => {
  refreshTable()
}
const handleSelectRows = (keys: string[]) => {
  selectedRowKeys.value = keys
}
const handleProductSelect = (product: SearchedProduct) => {
  const draftRef = draftRefs.value[`draft_${currentDraftUuid.value}`]

  draftRef.productSelect(product)

  closeSearchProductsDrawer()
}
const handleCloseApproveWindow = () => {
  approveWindowisOpened.value = false
  approveWindowPayload.value = null
}
const handleDraftApproved = async () => {
  handleCloseApproveWindow()
  try {
    await Promise.all([refreshTable(), syncStore.getSyncDraftsMeta(syncUuid)])
  } catch {
    message.error(i18n.t("synchronization.drafts_not_updated") as string)
  }
}
const closeSearchProductsDrawer = () => {
  searchProductsIsOpened.value = false
}
const onOfferDeleted = () => {
  refreshTable()
  syncStore.getSyncDraftsMeta(syncUuid)
}
// End Methods

// Lifecycle
onMounted(() => {
  refreshTable()
  syncStore.getSyncDraftsMeta(syncUuid)
  syncStore.getSynchronization(syncUuid)
  syncMetaStore.getAllActiveDeliveries()
})
onBeforeUpdate(() => {
  draftRefs.value = {}
})
// End Lifecycle
</script>

<style scoped lang="scss">
.drafts-summary {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.drafts-card {
  width: 420px;
  flex-shrink: 0;
}

.parse-errors-button {
  height: auto;
  padding: 8px 0 0;
  white-space: normal;
  text-align: left;
}

.drafts {
  &::v-deep tr.ant-table-row-selected td {
    background-color: transparent !important;
  }

  &::v-deep .ant-table-thead > tr > th.ant-table-selection-column {
    min-width: 36px !important;
    width: 36px !important;
    padding: 0 0 0 4px !important;
  }

  &::v-deep .ant-table-tbody > tr > td.ant-table-selection-column {
    min-width: 36px !important;
    width: 36px !important;
    padding: 0 0 0 4px !important;
  }

  &::v-deep .ant-table-expand-icon-th,
  &::v-deep .ant-table-row-expand-icon-cell {
    display: none;
  }

  &::v-deep .ant-table-expanded-row {
    & td:first-child {
      display: none;
    }
  }

  &::v-deep .ant-table-fixed > colgroup {
    & col:first-child {
      display: none;
    }
  }

  &::v-deep .ant-table-fixed {
    // Unset default vertical row border
    & tr > td {
      border-bottom: 0;
    }

    // Odd row
    & tr.ant-table-row.ant-table-row-level-0:nth-child(4n + 1) {
      background-color: #fff !important;
    }

    // Odd expanded row
    & tr.ant-table-expanded-row:nth-child(4n + 2) {
      background-color: #fff !important;

      & > td {
        border-bottom: 1px solid #e8e8e8;
      }
    }

    // Even row
    & tr.ant-table-row.ant-table-row-level-0 {
      background-color: #fafafa !important;
    }

    // Even expanded row
    & tr.ant-table-expanded-row {
      background-color: #fafafa !important;

      & > td {
        border-bottom: 1px solid #e8e8e8;
      }
    }

    // Uset hovered row styles
    & tr:hover:not(.ant-table-expanded-row) > td {
      background: transparent !important;
    }
  }

  &__thumbnail {
    padding: 3px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    width: 52px;
    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    color: #d9d9d9;
    font-size: 16px;
    z-index: 2;
    position: relative;
  }

  &__nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 25px 0;
  }

  &__left {
    display: flex;
    align-items: center;
  }

  &__right {
    display: flex;
    align-items: center;
    margin-left: 12px;
  }

  @media screen and (max-width: 1280px) {
    &__nav {
      align-items: start;
      flex-direction: column;
    }

    &__right {
      margin-left: 0;
      margin-top: 12px;
    }
  }

  &__tabs {
    position: relative;
    z-index: 9999;
    display: flex;
    justify-content: space-between;
  }

  &__inactive {
    color: #f5222d;
    display: block;
  }

  &__status {
    display: flex;
    flex-direction: column;

    &-row {
      display: flex;
      align-items: center;
    }

    &-subrow {
      display: flex;
      flex-direction: column;
    }
  }

  &__status-error {
    margin-left: 8px;
    font-size: 0;
  }

  &__pending-offer {
    color: #d48806;
  }

  &__for-sale {
    color: rgb(82, 196, 26);
  }

  &__xml-code {
    color: #afafaf;
    margin-right: 4px;
    margin-bottom: 2px;
    display: inline-block;
  }

  &__not-for-sale,
  &__empty-quantity {
    color: #f5222d;
  }
}
.tabs {
  display: flex;
  flex-direction: column;
  margin-bottom: -55px;

  &--no-margin {
    margin-bottom: 0;
  }

  &__tab-counter {
    margin-left: 8px;
  }
}
</style>
